import React, {useState, useEffect} from 'react';
import Select from 'react-select';
import {useTranslation} from 'react-i18next';
import './App.css';

function ByIdentifier() {
    const {t} = useTranslation();

    const [posts, setPosts] = useState([]);
    const [selectedPost, setSelectedPost] = useState(null);
    const [isPostValid, setIsPostValid] = useState(true);

    const [brokerDocId, setBrokerDocId] = useState('');
    const[brokerDocIdDisabled, setBrokerIdDisabled] = useState(false);
    const [isBrokerIdValid, setIsBrokerIdValid] = useState(true);
    const [brokerErrorText, setBrokerErrorText] = useState('');
    const [isLoadingBroker, setIsLoadingBroker] = useState(false);
    const handleBrokerDocIdChange = (event) => {
        const rawValue = event.target.value.replace(/\D/g, '');
        setBrokerDocId(rawValue);
    };

    const [commentStr, setCommentStr] = useState('');

    const [qrCodeUrl, setQrCodeUrl] = useState(null);
    const [isLoadingQr, setIsLoadingQr] = useState(false);

    const [amount, setAmount] = useState('');
    const [isAmountFetched, setIsAmountFetched] = useState(false);

    // If brokerDocId matches today's yy prefix, auto-select post '41700000' and make selector read-only
    const todayYY = new Date().getFullYear().toString().slice(-2);
    const brokerMatchesFormat = brokerDocId.trim() !== '' && brokerDocId.length === 13;

    useEffect(() => {
        if (brokerMatchesFormat) {
            // Try to find the post option from loaded posts, otherwise set a fallback label
            const found = posts.find(p => p.value === '41700000');
            if (found) {
                setSelectedPost(found);
            } else {
                setSelectedPost({ value: '41700000', label: '41700000' });
            }
            setIsPostValid(true);
        }
    }, [brokerMatchesFormat, posts]);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const baseUrl = process.env.REACT_APP_API_BASE_URL;
                const secretKey = process.env.REACT_APP_X_SECRET_KEY;
                const url = `${baseUrl}/posts`;
                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'X-Secret-Key': secretKey,
                    },
                });
                const data = await response.json();

                const sorted = data.slice().sort((a, b) => {
                    const aCode = (a.code || '').toString();
                    const bCode = (b.code || '').toString();
                    return aCode.localeCompare(bCode, undefined, { numeric: true });
                });

                const options = sorted.map(post => ({
                    value: post.code,
                    label: post.name
                }));
                setPosts(options);
            } catch (error) {
                console.error('Error fetching posts:', error);
            }
        };

        fetchPosts();
    }, []);

    const handlePostChange = (option) => {
        setSelectedPost(option);
        setIsPostValid(!!option);
    };

    const handleCheckBrokerDoc = async () => {
        const isPostValidNow = selectedPost !== null;
        const isBrokerIdFilled = brokerDocId.trim() !== '';

        setIsPostValid(isPostValidNow);
        setIsBrokerIdValid(isBrokerIdFilled);
        setBrokerErrorText(t('Enter broker id'));

        if (!isPostValidNow || !isBrokerIdFilled)
            return;

        setIsLoadingBroker(true);
        try {
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const secretKey = process.env.REACT_APP_X_SECRET_KEY;
            const url = `${baseUrl}/payment-charged/sum-by-broker-doc-id/${encodeURIComponent(brokerDocId)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'X-Secret-Key': secretKey,
                },
            });
            const data = await response.json();

            if (data === null) {
                setAmount(0);
                setIsAmountFetched(false);
                setBrokerErrorText(t('Broker id not found'));
                setIsBrokerIdValid(false);
            } else if (typeof data === 'number') {
                setAmount(data);
                setIsAmountFetched(true);
                setBrokerIdDisabled(true);
            } else if(data.messageDetail !== undefined){
                setIsBrokerIdValid(false);
                setBrokerErrorText(data.messageDetail);
            }
            else {
                console.error("Unexpected response:", data);
            }
        } catch (err) {
            console.error("Error fetching broker amount:", err);
        } finally {
            setIsLoadingBroker(false);
        }
    };

    const handleGenerateQR = async () => {

        if (!isAmountFetched || !selectedPost || !brokerDocId)
            return;

        setIsLoadingQr(true);

        const params = new URLSearchParams({
            postCode: selectedPost.value,
            postName: selectedPost.label,
            brokerDocId: brokerDocId,
            amount: amount,
            comment: commentStr,
        });

        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const secretKey = process.env.REACT_APP_X_SECRET_KEY;
        const url = `${baseUrl}/qr/by-identifier?${params.toString()}`;

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    Accept: 'image/png',
                    'X-Secret-Key': secretKey,
                },
            });

            if(response.status !== 200)
            {
                const data = await response.json();
                if(data.messageDetail !== undefined){
                    setIsBrokerIdValid(false);
                    setBrokerErrorText(data.messageDetail);
                };
                return;
            }

            if (!response.ok)
                throw new Error(`HTTP error: ${response.status}`)

                        const blob = await response.blob();
            if (qrCodeUrl) {
                URL.revokeObjectURL(qrCodeUrl);
            }

            const imageUrl = URL.createObjectURL(blob);
            setQrCodeUrl(imageUrl);
        } catch (err) {
            console.error('QR generation error:', err);
        } finally {
            setIsLoadingQr(false);
        }
    };

    const wrapWordStyle = {
        control: (provided) => ({
            ...provided,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            minHeight: '38px',
            alignItems: 'flex-start',
            paddingTop: '4px',
            paddingBottom: '4px',
            width: '100%',
            maxWidth: '100%',
        }),
        singleValue: (provided) => ({
            ...provided,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '1.2',
        }),
        option: (provided) => ({
            ...provided,
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            padding: 10,
            maxWidth: '500px', // keep this if you want long options to wrap
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            maxWidth: '500px',
            width: '100%',
        }),
    };

    return (
        <div className="input-container">
            <div className="form-group">
                <div className="form-label">{t('Customs')}</div>
                <div style={{flex: 1}}>
                    <Select
                        value={selectedPost}
                        onChange={handlePostChange}
                        options={posts}
                        placeholder={t('Select a customs')}
                        isClearable
                        isSearchable
                        isDisabled={brokerMatchesFormat}
                        styles={{
                            ...wrapWordStyle,
                            control: (base, state) => ({
                                ...wrapWordStyle.control(base, state),
                                borderColor: isPostValid ? base.borderColor : 'red',
                                boxShadow: isPostValid ? base.boxShadow : '0 0 0 1px red',
                            }),
                        }}
                    />
                </div>
            </div>
            {!isPostValid && (
                <div className="form-group">
                    <div className="input-label"></div>
                    <div className="error-text">{t('Please select a customs')}</div>
                </div>
            )}

            <div className="form-group">
                <div className="form-label">{t('Broker Document Id')}</div>
                <input
                    type="text"
                    value={brokerDocId}
                    disabled={brokerDocIdDisabled}
                    onChange={handleBrokerDocIdChange}
                    placeholder={t('Enter broker id')}
                    style={{border: isBrokerIdValid ? '1px solid #ccc' : '1px solid red'}}
                />
            </div>
            {!isBrokerIdValid && (
                <div className="form-group">
                    <div className="input-label"></div>
                    <div className="error-text">{brokerErrorText}</div>
                </div>
            )}

            <div className="form-group">
                <div className="form-label">{t('Commentary')}</div>
                <input
                    type="text"
                    value={commentStr}
                    onChange={(e) => setCommentStr(e.target.value)}
                    placeholder={t('Enter the comment')}
                />
            </div>

            <div className="form-group">
                <div className="input-label"></div>
                <button
                    className="form-btn"
                    onClick={handleCheckBrokerDoc}
                    disabled={isLoadingBroker || isLoadingQr || qrCodeUrl}
                >{isLoadingBroker ? (
                    <>
                        <span className="spinner"/> {t('Loading...')}
                    </>
                ) : (
                    t('Determinate sum')
                )}</button>
            </div>

            {isAmountFetched && (
                <>
                    <div className="form-group">
                        <div className="input-label">{t('Amount')}</div>
                        <input
                            type="text"
                            value={amount}
                            readOnly
                            disabled
                        />
                    </div>

                    <div className="form-group">
                        <div className="form-label"></div>
                        <button className="form-btn"
                            onClick={handleGenerateQR}
                                disabled={isLoadingQr}
                        >{isLoadingQr ? (
                            <>
                                <span className="spinner"/> {t('Loading...')}
                            </>
                        ) : (
                            t('Generate QR')
                        )}
                        </button>
                    </div>

                    {qrCodeUrl && (
                        <div
                            style={{display: "flex", flexDirection: "column", alignItems: "center", marginTop: 20}}>
                            <h3>{t('Pay QR')}</h3>
                            <img src={qrCodeUrl} alt="QR Code"/>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default ByIdentifier;
